import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, useWatch } from 'react-hook-form';
import type { Control, Path } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { useCurrentUser } from '@/lib/auth-context';
import { applyApiFieldErrors } from '@/lib/form-errors';
import { hasPermission } from '@/lib/permissions';

import { PermissionChecklist } from './PermissionChecklist';
import {
  useCompanyOptions,
  useCreateUser,
  useFacilityOptions,
  usePermissionCatalog,
  useUpdateUser,
} from './useUsers';
import {
  userCreateSchema,
  userUpdateSchema,
  type EfsUser,
  type UserCreateInput,
  type UserUpdateInput,
} from './types';

const NS = 'settings';

// Sentinel string used as the Select value when the user picks "no
// company / no facility" — shadcn `<Select>` cannot bind `''` to an item.
const NONE_VALUE = '__none__';

interface UserFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  row?: EfsUser | null;
}

export const UserFormDialog = ({ open, onOpenChange, row }: UserFormDialogProps) => {
  const { t } = useTranslation(NS);
  const isEdit = !!row;
  const me = useCurrentUser();
  // Only actors who may create OUTSIDE their own company get a company picker:
  // a company-scoped admin is pinned to their own company by the backend, and
  // master.manage happens to imply company.read, so the old check offered them
  // a dropdown of other companies that always 403'd on submit.
  const canPickCompany =
    hasPermission(me, 'system.admin') ||
    (me?.partyType === 'platform' && hasPermission(me, 'user.manage'));
  const canPickFacility =
    hasPermission(me, 'facility.read') || hasPermission(me, 'facility.create');
  const companies = useCompanyOptions(open && canPickCompany);
  const facilities = useFacilityOptions(open && canPickFacility);
  const create = useCreateUser();
  const update = useUpdateUser();

  if (isEdit && row) {
    return (
      <EditForm
        row={row}
        open={open}
        onOpenChange={onOpenChange}
        companies={companies.data ?? []}
        facilities={facilities.data ?? []}
        onSubmit={async (values) => {
          await update.mutateAsync({ id: row.user_id, body: values });
          onOpenChange(false);
        }}
        isPending={update.isPending}
        // Company MOVES are Global-scope only on the backend — offering the
        // picker to operator managers would just harvest 403s.
        showCompany={hasPermission(me, 'system.admin')}
        showFacility={canPickFacility}
        facilitiesLoaded={facilities.isSuccess}
        t={t}
      />
    );
  }

  return (
    <CreateForm
      open={open}
      onOpenChange={onOpenChange}
      companies={companies.data ?? []}
      facilities={facilities.data ?? []}
      onSubmit={async (values) => {
        await create.mutateAsync(values);
        onOpenChange(false);
      }}
      isPending={create.isPending}
      showCompany={canPickCompany}
      showFacility={canPickFacility}
      facilitiesLoaded={facilities.isSuccess}
      ownCompanyId={me?.companyId ?? undefined}
      t={t}
    />
  );
};

interface CreateFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companies: { company_id: string; display_name: string }[];
  facilities: { facility_id: string; facility_name: string; company_id: string }[];
  onSubmit: (values: UserCreateInput) => Promise<void>;
  isPending: boolean;
  showCompany: boolean;
  showFacility: boolean;
  facilitiesLoaded: boolean;
  /** The actor's own company — scopes the facility list when the company
   * picker is hidden (a company-scoped admin creates only in-house). */
  ownCompanyId?: string;
  t: ReturnType<typeof useTranslation>['t'];
}

const STEP1_FIELDS = ['email', 'display_name', 'password'] as const;

const CreateForm = ({
  open,
  onOpenChange,
  companies,
  facilities,
  onSubmit,
  isPending,
  showCompany,
  showFacility,
  facilitiesLoaded,
  ownCompanyId,
  t,
}: CreateFormProps) => {
  const defaults: UserCreateInput = {
    email: '',
    display_name: '',
    password: '',
    company_id: '',
    facility_id: '',
    permission_codes: [],
  };

  const [step, setStep] = useState<1 | 2>(1);
  const form = useForm<UserCreateInput>({
    resolver: zodResolver(userCreateSchema),
    defaultValues: defaults,
  });

  const selectedCompany = useWatch({ control: form.control, name: 'company_id' });

  useEffect(() => {
    if (!open) {
      form.reset(defaults);
      setStep(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // The catalog is party-scoped, so ticks made under one company are invalid
  // for another — clear them whenever the company changes.
  useEffect(() => {
    form.setValue('permission_codes', []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany]);

  // Vendor admins (no company picker) always browse their own catalog;
  // privileged actors need a selected company first.
  const catalog = usePermissionCatalog(showCompany ? selectedCompany || undefined : undefined, {
    enabled: open && (!showCompany || !!selectedCompany),
  });
  const noCompanyChosen = showCompany && !selectedCompany;

  const goNext = async () => {
    const ok = await form.trigger([...STEP1_FIELDS]);
    if (ok) setStep(2);
  };

  const submitStep2 = form.handleSubmit(async (values) => {
    form.clearErrors();
    try {
      await onSubmit(values);
    } catch (error) {
      applyApiFieldErrors(form, error, [
        'email',
        'display_name',
        'password',
        'company_id',
        'facility_id',
        'permission_codes',
      ]);
      // Surface credential-side errors (e.g. duplicate email) on their step.
      if (STEP1_FIELDS.some((field) => form.getFieldState(field).error)) setStep(1);
    }
  });

  const submit = (event: FormEvent<HTMLFormElement>) => {
    // Enter on step 1 advances instead of creating with zero permissions.
    if (step === 1) {
      event.preventDefault();
      void goNext();
      return;
    }
    void submitStep2(event);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{t('efs.settings.users.dialog.create_title')}</SheetTitle>
          <SheetDescription>{t('efs.settings.users.dialog.create_subtitle')}</SheetDescription>
          <p className="text-xs text-efs-text-muted">
            {t('efs.settings.users.dialog.step_indicator', { current: step, total: 2 })}
          </p>
        </SheetHeader>

        <Form {...form}>
          <form onSubmit={submit} className="mt-6 space-y-4" noValidate>
            <div className={step === 1 ? 'space-y-4' : 'hidden'}>
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('efs.settings.users.fields.email')}</FormLabel>
                    <FormControl>
                      <Input type="email" autoFocus {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="display_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('efs.settings.users.fields.display_name')}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('efs.settings.users.fields.password')}</FormLabel>
                    <FormControl>
                      <Input type="password" autoComplete="new-password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {showCompany ? (
                <CompanyField control={form.control} companies={companies} t={t} />
              ) : null}
              {showFacility ? (
                <FacilityField
                  control={form.control}
                  facilities={facilities}
                  facilitiesLoaded={facilitiesLoaded}
                  fallbackCompanyId={showCompany ? undefined : ownCompanyId}
                  t={t}
                />
              ) : null}
            </div>

            <div className={step === 2 ? 'space-y-3' : 'hidden'}>
              <div className="text-sm font-medium text-efs-text-primary">
                {t('efs.settings.users.dialog.permissions_title')}
              </div>
              {noCompanyChosen ? (
                <p className="rounded-md bg-efs-bg-subtle px-3 py-2 text-xs text-efs-text-muted">
                  {t('efs.settings.users.messages.no_company_permissions')}
                </p>
              ) : catalog.isLoading ? (
                <p className="py-6 text-center text-sm text-efs-text-muted">
                  {t('efs.common.messages.loading', { ns: 'common' })}
                </p>
              ) : (
                <FormField
                  control={form.control}
                  name="permission_codes"
                  render={({ field }) => (
                    <FormItem>
                      <PermissionChecklist
                        catalog={catalog.data ?? []}
                        value={field.value}
                        onChange={field.onChange}
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>

            {form.formState.errors.root?.message ? (
              <p className="text-sm font-medium text-destructive">
                {String(form.formState.errors.root.message)}
              </p>
            ) : null}

            <SheetFooter className="pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isPending}
              >
                {t('efs.common.actions.cancel')}
              </Button>
              {step === 1 ? (
                <Button type="button" onClick={() => void goNext()}>
                  {t('efs.settings.users.actions.next')}
                </Button>
              ) : (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep(1)}
                    disabled={isPending}
                  >
                    {t('efs.settings.users.actions.back')}
                  </Button>
                  <Button type="submit" disabled={isPending}>
                    {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    {t('efs.settings.users.actions.create')}
                  </Button>
                </>
              )}
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
};

interface EditFormProps {
  row: EfsUser;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companies: { company_id: string; display_name: string }[];
  facilities: { facility_id: string; facility_name: string; company_id: string }[];
  onSubmit: (values: UserUpdateInput) => Promise<void>;
  isPending: boolean;
  showCompany: boolean;
  showFacility: boolean;
  facilitiesLoaded: boolean;
  t: ReturnType<typeof useTranslation>['t'];
}

const EditForm = ({
  row,
  open,
  onOpenChange,
  companies,
  facilities,
  onSubmit,
  isPending,
  showCompany,
  showFacility,
  facilitiesLoaded,
  t,
}: EditFormProps) => {
  const defaults: UserUpdateInput = {
    display_name: row.display_name,
    active_flag: row.active_flag,
    ...(showCompany ? { company_id: row.company_id ?? '' } : {}),
    ...(showFacility ? { facility_id: row.facility_id ?? '' } : {}),
  };

  const form = useForm<UserUpdateInput>({
    resolver: zodResolver(userUpdateSchema),
    defaultValues: defaults,
  });

  useEffect(() => {
    if (open) {
      form.reset({
        display_name: row.display_name,
        active_flag: row.active_flag,
        ...(showCompany ? { company_id: row.company_id ?? '' } : {}),
        ...(showFacility ? { facility_id: row.facility_id ?? '' } : {}),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, row]);

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors();
    try {
      await onSubmit(values);
    } catch (error) {
      applyApiFieldErrors(form, error, [
        'display_name',
        'company_id',
        'facility_id',
        'active_flag',
      ]);
    }
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{t('efs.settings.users.dialog.edit_title')}</SheetTitle>
          <SheetDescription>{row.email}</SheetDescription>
        </SheetHeader>

        <Form {...form}>
          <form onSubmit={submit} className="mt-6 space-y-4" noValidate>
            <FormField
              control={form.control}
              name="display_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('efs.settings.users.fields.display_name')}</FormLabel>
                  <FormControl>
                    <Input autoFocus {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {showCompany ? (
              <CompanyField control={form.control} companies={companies} t={t} />
            ) : null}
            {showFacility ? (
              <FacilityField
                control={form.control}
                facilities={facilities}
                facilitiesLoaded={facilitiesLoaded}
                // Only when the picker is hidden: with a picker on screen an
                // explicit "no company" must stay empty, or the old company
                // would keep validating a facility the save then rejects.
                fallbackCompanyId={showCompany ? undefined : (row.company_id ?? undefined)}
                t={t}
              />
            ) : null}
            <FormField
              control={form.control}
              name="active_flag"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center gap-2 space-y-0">
                  <FormControl>
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  </FormControl>
                  <FormLabel className="text-sm">{t('efs.settings.users.fields.active')}</FormLabel>
                </FormItem>
              )}
            />

            {form.formState.errors.root?.message ? (
              <p className="text-sm font-medium text-destructive">
                {String(form.formState.errors.root.message)}
              </p>
            ) : null}

            <SheetFooter className="pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isPending}
              >
                {t('efs.common.actions.cancel')}
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {t('efs.common.actions.save')}
              </Button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
};

interface CompanyFieldProps<T extends { company_id?: string }> {
  control: Control<T>;
  companies: { company_id: string; display_name: string }[];
  t: ReturnType<typeof useTranslation>['t'];
}

const CompanyField = <T extends { company_id?: string }>({
  control,
  companies,
  t,
}: CompanyFieldProps<T>) => (
  <FormField
    control={control}
    name={'company_id' as Path<T>}
    render={({ field }) => (
      <FormItem>
        <FormLabel>{t('efs.settings.users.fields.company')}</FormLabel>
        <FormControl>
          <Select
            value={(field.value as string | undefined) ? (field.value as string) : NONE_VALUE}
            onValueChange={(v) => field.onChange(v === NONE_VALUE ? '' : v)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE_VALUE}>—</SelectItem>
              {companies.map((c) => (
                <SelectItem key={c.company_id} value={c.company_id}>
                  {c.display_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormControl>
        <FormMessage />
      </FormItem>
    )}
  />
);

interface FacilityFieldProps<T extends { facility_id?: string; company_id?: string }> {
  control: Control<T>;
  facilities: { facility_id: string; facility_name: string; company_id: string }[];
  /** False while the facility list is still loading — the mismatch check
   * cannot run against an empty list without wiping a valid selection. */
  facilitiesLoaded: boolean;
  /** Company to scope by when the form carries no company picker (edit mode
   * for non-super-admins) — without it the field would find no valid
   * facilities and clear the account's existing one. */
  fallbackCompanyId?: string;
  t: ReturnType<typeof useTranslation>['t'];
}

const FacilityField = <T extends { facility_id?: string; company_id?: string }>({
  control,
  facilities,
  facilitiesLoaded,
  fallbackCompanyId,
  t,
}: FacilityFieldProps<T>) => {
  // Facilities belong under a company, so scope the dropdown to the selected
  // company. Until a company is chosen the list is empty (a facility cannot be
  // assigned without its owning company).
  const watchedCompany = useWatch({ control, name: 'company_id' as Path<T> }) as string | undefined;
  const selectedCompany = watchedCompany || fallbackCompanyId;
  const scoped = selectedCompany ? facilities.filter((f) => f.company_id === selectedCompany) : [];

  return (
    <FormField
      control={control}
      name={'facility_id' as Path<T>}
      render={({ field }) => {
        const current = field.value as string | undefined;
        const isValid =
          !current || !facilitiesLoaded || scoped.some((f) => f.facility_id === current);
        return (
          <FacilityFieldInner
            value={current}
            isValid={isValid}
            scoped={scoped}
            disabled={!selectedCompany}
            onChange={(v) => field.onChange(v)}
            t={t}
          />
        );
      }}
    />
  );
};

// Split out so the "clear stale facility when company changes" runs in an
// effect (never a state update during render).
const FacilityFieldInner = ({
  value,
  isValid,
  scoped,
  disabled,
  onChange,
  t,
}: {
  value: string | undefined;
  isValid: boolean;
  scoped: { facility_id: string; facility_name: string }[];
  disabled: boolean;
  onChange: (v: string) => void;
  t: ReturnType<typeof useTranslation>['t'];
}) => {
  // If the chosen facility no longer belongs to the selected company, clear it
  // so a mismatched (company, facility) pair is never submitted.
  useEffect(() => {
    if (!isValid) onChange('');
  }, [isValid, onChange]);

  return (
    <FormItem>
      <FormLabel>{t('efs.settings.users.fields.facility')}</FormLabel>
      <FormControl>
        <Select
          value={value && isValid ? value : NONE_VALUE}
          onValueChange={(v) => onChange(v === NONE_VALUE ? '' : v)}
          disabled={disabled}
        >
          <SelectTrigger>
            <SelectValue
              placeholder={
                disabled
                  ? t('efs.settings.users.facility_pick_company', {
                      defaultValue: '先に会社を選択してください',
                    })
                  : undefined
              }
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE_VALUE}>—</SelectItem>
            {scoped.map((f) => (
              <SelectItem key={f.facility_id} value={f.facility_id}>
                {f.facility_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormControl>
      <FormMessage />
    </FormItem>
  );
};
